import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { DateRange } from 'react-day-picker';

dayjs.extend(utc);

export type Preset = {
  label: string;
  getValue: () => DateRange;
};

export type TimeUnitObject = {
  h: string;
  m: string;
};

export type TimeUnitType = 'h' | 'm';
export type RangeBoundaryType = 'start' | 'end';

export const PRESETS: Preset[] = [
  {
    label: 'Last hour',
    getValue: () => ({
      from: dayjs().utc().subtract(1, 'hour').toDate(),
      to: dayjs.utc().toDate(),
    }),
  },
  {
    label: 'Last 4 hours',
    getValue: () => ({
      from: dayjs().utc().subtract(4, 'hour').toDate(),
      to: dayjs.utc().toDate(),
    }),
  },
  {
    label: 'Last 8 hours',
    getValue: () => ({
      from: dayjs().utc().subtract(8, 'hour').toDate(),
      to: dayjs.utc().toDate(),
    }),
  },
  {
    label: 'Last 24 hours',
    getValue: () => ({
      from: dayjs().utc().subtract(24, 'hour').toDate(),
      to: dayjs.utc().toDate(),
    }),
  },
  {
    label: 'Last 48 hours',
    getValue: () => ({
      from: dayjs().utc().subtract(48, 'hour').toDate(),
      to: dayjs.utc().toDate(),
    }),
  },
  {
    label: 'Last 7 days',
    getValue: () => ({
      from: dayjs().utc().subtract(7, 'day').toDate(),
      to: dayjs.utc().toDate(),
    }),
  },
];

export const formatRange = (range: DateRange | undefined) => {
  if (!range?.from) return 'Pick a time range';

  const from = dayjs.utc(range.from);
  const to = dayjs.utc(range.to ?? range.from);

  const startStr = from.format('HH:mm');
  const endStr = to.format('HH:mm');

  return `${from.format('MMM D YYYY')} ${startStr} UTC - ${to.format('MMM D YYYY')} ${endStr} UTC`;
};

export const getUtcTimeState = (date: Date | undefined, boundary: RangeBoundaryType = 'start') => {
  if (!date) {
    return boundary === 'start' ? { h: '00', m: '00' } : { h: '23', m: '59' };
  }
  const d = dayjs.utc(date);
  return { h: d.format('HH'), m: d.format('mm') };
};

export const getDisplayMonth = (date?: Date) =>
  dayjs
    .utc(date ?? dayjs.utc())
    .subtract(1, 'month')
    .toDate();
